import { MelodyLib, VocalLib, hardestLeap, phraseHeadline, type PhraseVerdict } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { midBarCut, midBarLabels, type PracticeTarget, type Singable } from './practice-target.js'
import { sungNoteName } from './sung-note-name.js'

/**
 * What the phrase feedback shows, in Joni §7's fixed order (plan M6a step 6.4): one headline, the notes that
 * were not on, and the hardest spot. Pure: every string and every rule is tested here without a screen.
 */

export interface OffNote {
  referenceIndex: number
  /** The number the roll marks the note with */
  mark: string
  /** "B4 in bar 10 — 30 cents flat", "Note 3 (on Am) — not heard" */
  text: string
}

export interface FeedbackView {
  /** Null when no verdict fires: the score line alone, and the blocks below carry the feedback */
  headline: PhraseVerdict | null
  scorePercent: number
  offNotes: OffNote[]
  /** Audition note index → its mark, for the roll */
  marks: Record<number, string>
  hardest?: { verdict: PhraseVerdict; where: string; audition: Melody }
  /** Joni §8, on `vocal-phrase-too-long` only */
  split?: { text: string; halves: Singable[] }
}

/** Joni §7's "on" band */
const onCents = 20

const centsText = (cents: number) => {
  const shown = VocalLib.wholeCents(cents)
  return `${shown} cents ${cents < 0 ? 'flat' : 'sharp'}`
}

/**
 * What was sung for a wrong note: "F4", spelled by Bowie's rule (`sungNoteName`), or null when it sits
 * between two. Bowie, on M6a: within the close band (40 ¢), so the name and the bands agree.
 */
const sungName = (score: VocalLib.PhraseScore, target: PracticeTarget, referenceIndex: number, cents: number) => {
  const reference = target.reference[referenceIndex]
  if (reference === undefined) {
    return null
  }
  const sung = reference + cents / 100 + score.octave * 12
  const midi = Math.round(sung)
  if (Math.abs(sung - midi) * 100 > VocalLib.closeBandCents) {
    return null
  }
  const chord = target.notes[referenceIndex]?.chord
  return sungNoteName(midi, {
    ...(target.spellingKey ? { key: target.spellingKey } : {}),
    ...(chord ? { chord } : {}),
    isBelow: cents < 0,
  })
}

/** The first audition note of each reference note: a merged event's first note, or a chord tone's own */
const auditionIndexOf = (target: PracticeTarget) => {
  if (target.singable.kind !== 'phrase') {
    return (referenceIndex: number) => referenceIndex
  }
  /** The slice holds the events' notes in order, so an event starts after all the notes before it */
  const offsets: number[] = []
  let offset = 0
  for (const event of target.singable.phrase.events) {
    offsets.push(offset)
    offset += event.noteIndices.length
  }
  return (referenceIndex: number) => offsets[referenceIndex] ?? referenceIndex
}

const offNotesOf = (score: VocalLib.PhraseScore, target: PracticeTarget) => {
  const paired = score.referenceIndices.flatMap((referenceIndex, index) => {
    const note = score.absolute.perNote[index]
    if (note.band === 'wrong-note') {
      const name = sungName(score, target, referenceIndex, note.cents)
      return [{ referenceIndex, detail: name ? `sung as ${name}` : 'a different note' }]
    }
    return VocalLib.wholeCents(note.cents) > onCents ? [{ referenceIndex, detail: centsText(note.cents) }] : []
  })
  const missed = score.missed.map(referenceIndex => ({ referenceIndex, detail: 'not heard' }))
  return [...paired, ...missed]
    .sort((a, b) => a.referenceIndex - b.referenceIndex)
    .map(({ referenceIndex, detail }, index): OffNote => ({
      referenceIndex,
      mark: String(index + 1),
      text: `${target.notes[referenceIndex]?.label ?? `Note ${referenceIndex + 1}`} — ${detail}`,
    }))
}

/** The two notes of a leap, as a melody of their own from beat 0 */
const twoNotes = (target: PracticeTarget, notes: [number, number]): Melody => {
  const at = auditionIndexOf(target)
  const picked = notes.flatMap(referenceIndex => {
    const note = target.audition.notes[at(referenceIndex)]
    return note ? [note] : []
  })
  const start = picked[0]?.onset ?? 0
  return { ...target.audition, notes: picked.map(note => ({ ...note, onset: note.onset - start })) }
}

const hardestOf = (score: VocalLib.PhraseScore, target: PracticeTarget): FeedbackView['hardest'] => {
  const leap = hardestLeap(
    score,
    target.notes.map(note => note.name)
  )
  if (!leap) {
    return undefined
  }
  const { notes, ...verdict } = leap
  const { singable } = target
  const where =
    singable.kind === 'phrase'
      ? `bar ${singable.phrase.events[notes[0]]?.bar ?? 1}`
      : `notes ${notes[0] + 1}–${notes[1] + 1}`
  return { verdict, where, audition: twoNotes(target, notes) }
}

const barsOf = (phrase: MelodyLib.Phrase) =>
  phrase.source.startBar === phrase.source.endBar
    ? `${phrase.source.startBar}`
    : `${phrase.source.startBar}–${phrase.source.endBar}`

/** Joni §8: cut at the phrase's strongest internal cut point; chord-only material by chart line */
const splitOf = (target: PracticeTarget, range: VocalLib.VocalRange | null): FeedbackView['split'] => {
  const { singable } = target
  if (singable.kind === 'phrase') {
    const key = singable.source.melody.key ?? singable.source.key
    const halves = MelodyLib.splitPhrase(singable.source.melody, singable.phrase, key ? { key } : {})
    if (!halves) {
      return undefined
    }
    const [first, second] = halves
    const firstLabel = first.source.startBar === first.source.endBar ? 'bar' : 'bars'
    const onset = second.events[0]?.onset
    const cut = onset === undefined ? undefined : midBarCut(singable.source.melody.tempo.meter, onset)
    return {
      text: cut ? midBarLabels(cut).split : `Split it in two: ${firstLabel} ${barsOf(first)}, then ${barsOf(second)}.`,
      halves: halves.map(phrase => ({ kind: 'phrase', source: singable.source, phrase })),
    }
  }
  const { first, last } = singable.part.lines
  if (first === last) {
    return undefined
  }
  const lineHalves = [first, last].flatMap(lineNumber => {
    const line = singable.source.lines.find(entry => entry.lineNumber === lineNumber)
    if (!line) {
      return []
    }
    const parts = VocalLib.chartParts({ name: singable.source.section, lines: [line] }, range ?? { low: 55, high: 67 })
    const [part] = singable.kind === 'guide-tones' ? parts.guideTones : parts.outlines
    return part ? [{ kind: singable.kind, source: singable.source, part }] : []
  })
  return lineHalves.length === 2
    ? { text: `Split it in two: line ${first}, then line ${last}.`, halves: lineHalves }
    : undefined
}

export const feedbackView = (
  score: VocalLib.PhraseScore | null,
  target: PracticeTarget,
  range: VocalLib.VocalRange | null
): FeedbackView | null => {
  if (!score) {
    return null
  }
  const headline = phraseHeadline(score, {
    keyChange: target.keyChange,
    ...(target.songKey ? { songKey: target.songKey } : {}),
    ...(target.practiceKey ? { practiceKey: target.practiceKey } : {}),
  })
  const offNotes = offNotesOf(score, target)
  const at = auditionIndexOf(target)
  const isOnPitch = headline?.id === 'vocal-phrase-on-pitch'
  const split = headline?.id === 'vocal-phrase-too-long' ? splitOf(target, range) : undefined
  const hardest = isOnPitch ? undefined : hardestOf(score, target)
  return {
    headline,
    scorePercent: Math.round(score.absolute.mean * 100),
    offNotes,
    marks: Object.fromEntries(offNotes.map(note => [at(note.referenceIndex), note.mark])),
    ...(hardest ? { hardest } : {}),
    ...(split ? { split } : {}),
  }
}
